// Utilities extracted from ClassRoomModule.js

import _ from "lodash";
import update from "immutability-helper";
import React from "react";
import {
  sendStudentAssignmentSubmissionFeedbackMutation,
  updateUserMessageStateMutation,
  sendPdftronCommentAddedMutation,
  createEntityEventTrackingMutation,
  deleteJobMutation,
  keepStudentAssignmentAIEvaluationMutation,
  validateAssessmentLinkMutation,
} from "../ClassRoomMutation";
import {
  getCourseAssignmentFeedQuery,
  getAssignmentDeletedStatusQuery,
  getStudentAiEvaluationJobStatusQuery,
} from "../ClassRoomQuery";
import {
  getStudentAssignmentDetailsFromCache,
  writeStudentAssignmentDetailsInCache,
} from "../ClassRoomGraphqlHelpers";
import {
  getGradebookAllAssessmentsVariables,
  getGradebookCategoryWiseAssessmentsVariables,
  getGradebookStudentWiseRatingsVariables,
  getCoursePrimaryGradeIdFromCache,
  getGradebookAssignmentsVariables,
} from "Gradebook/modules/GradebookGraphqlHelpers";
import client from "lib/apolloClient/client";
import {
  clearLocalStorageForKey,
  setEncryptedJsonItem,
  getDecryptedJsonItem,
} from "services/localStorage";
import {
  ATTACHMENT_WARNINGS,
  generateRandomId,
  getFileExtensionFromMimeType,
  isUserTypeStaff,
} from "Utils";
import { getConversationsUnreadCountQuery } from "AppComponents/ChatContainer/modules/ChatContainerQuery";
import {
  getStudentWiseRatingsQuery,
  getCategoryWiseAssessmentsQuery,
  getOrganizationAssignmentsGradebookQuery,
  getGradebookAssignmentsQuery,
} from "Gradebook/modules/GradebookQueries";
import {
  SUBMISSION_OPTIONS_V2,
  INCLUDE_ITEM_TYPES_FOR_UNREAD_MESSAGES_COUNT,
  INCLUDE_ITEM_TYPES_FOR_STUDENT_UNREAD_MESSAGES_COUNT,
} from "./ClassRoomConstants";
import ACLStore from "lib/aclStore";
import { CreateAssessmentSvg } from "SvgComponents";
import { ImportOutlined } from "@toddle-edu/ds-icons";
import { colors } from "Constants";
import moment from "moment";
import { EXCLUDED_ASSESSMENT_TYPES } from "store/static";
import {
  CREATE_AI_TUTOR,
  SCORE,
  GRADE_SCALE_TOOL,
} from "Constants/stringConstants";
import { getUpdatedToolResponses } from "AssessmentEvaluation/modules/AssessmentEvaluationModule";
import { USE_NEW_GRADING_SETUP_IN_COURSE_PERM } from "Constants/permissionConstants";
import {
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getIsStudent,
} from "modules/CommonSelectors";
import {
  getIsConfigurationEnabledForCPAY,
  getSettingValue,
} from "modules/PermissionModule";
import { checkIsDateSame } from "Utils/DateTimeHelpers";
import { getIsGroupByCategoryView } from "AssessmentsGradebookV2/components/ConfigureViews/components/DisplaySettings/DisplaySettingsUtils";
import { ASSIGNMENT_TASK_STATUS_MAPPING } from "../Constants";
import {
  getIsCourseBasedGradingEnabled,
  getIsLearningCoursesEnabled,
} from "Teacher/routes/LearningCourses/modules/Utils";
import { getGroupOrAssessmentTypes } from "AppComponents/Gradebook/modules/GradebookGraphqlHelpers";
import { splitAssessmentAndResourceTypesMemoized } from "Utils/EntityFlowUtils/EntityHelpers";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";
import {
  getStudentAssignmentFromCacheV2,
  writeStudentAssignmentInCacheV2,
} from "Worksheet/modules/WorksheetGraphqlHelpers";
import { getAssessmentTemplatesFromCache } from "ChildrenPage/modules/ChildrenPageGraphqlHelpers";
import {
  getIsEnabledExcusedStatusInAssignment,
  getIsEnabledAssignmentImprovements,
} from "Utils/ACLStoreUtils";
import {
  ASSIGNMENT_STATE_TABS,
  BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF,
  PYP_ASSIGNMENT_TYPE_MAPPING_FOR_CALENDAR,
  MYP_ASSIGNMENT_TYPE_MAPPING_FOR_CALENDAR,
  UBD_ASSIGNMENT_TYPE_MAPPING_FOR_CALENDAR,
  VIDEO_MEETING,
  ARCHIVED_STUDENT_ASSIGNMENT_STATUS,
  ARCHIVED_STUDENT_ASSIGNMENT_STATUS_V2,
} from "./ClassRoomConstants";

// Helper function to check if current route is media manager

export const getIsMediaManagerRoute = () => {
  const currentPath = _.get(window, "location.pathname", "");
  return currentPath.search("mediamanager") > -1;
};

export const getCourseFlowFeedQueryVariables = ({ state }) => {
  const learningCourseId = getSelectedLearningCourseId(state);

  const curriculumType = getCurrentCurriculumProgramType(state);

  const filtersInState = _.get(state, "courseFlow.filters", {});
  const courseFlowFilters = _.mapValues(filtersInState, (value, key) => {
    const isAllOption = _.isEqual(value, "ALL") && key != "searchText";
    return isAllOption ? [] : value;
  });

  const {
    searchText = "",
    unitTypes = [],
    students = [],
    contentTypes = [],
    classes = [],
  } = courseFlowFilters;

  const {
    selectedAssessmentTypes,
    selectedResourceTypes,
  } = splitAssessmentAndResourceTypesMemoized({
    selectedItemTypes: contentTypes,
  });

  const { assessmentTypes = [], groupTypes = [] } = getGroupOrAssessmentTypes({
    curriculumType,
    selectedAssessmentTypes,
  });

  const assessmentFilters = {
    assessmentType: assessmentTypes,
    groupTypes,
  };

  const filters = {
    searchText,
    unitTypes,
    sharedWith: students,
    itemTypes: ["FOLDER", "ASSESSMENT", "UNIT_PLAN", "FILE"],
    classIds: classes,
    assessmentFilters,
    attachmentTypes: selectedResourceTypes,
  };

  return {
    learningCourseId,
    filters,
    syncedBluePrintItemFilters: {
      learningCourseId,
    },
  };
};

const getMutationParams = ({
  params,
  creationMode,
  isUnitDelinked = false,
  isDuplicateAssignment = false,
  isFromClassMaterials = false,
}) => {
  let newParams = params;

  // when creating from class flow, we want to add to org resources manually
  // so that we can fetch the resource and update the cache
  if (isFromClassMaterials) {
    newParams = update(params, {
      isSharedWithClassMaterial: {
        $set: false,
      },
    });
  }

  // for creating le, smt and fmt from classroom, we have to send importedLeId instead of
  // leId, so that backend creates distinct copies for all the assigned classes. If leId is
  // sent instead of importedLeId, then backend does not creates a copy for the first assigned class
  switch (creationMode) {
    case "CREATE_FA":
    case "CREATE_SA":
    case "CREATE_LE":
    case "CREATE_ASSESSMENT":
    case "CREATE_WORKSHEET":
    case CREATE_AI_TUTOR:
      if (!isUnitDelinked && !getIsLearningCoursesEnabled()) {
        newParams = update(newParams, {
          importedLeId: {
            $set: params.leId,
          },
          leId: {
            $set: "",
          },
        });
      }
      return _.omit(newParams, ["creationMode", "creationSource", "pageName"]);
    default:
      if (isDuplicateAssignment && !isUnitDelinked) {
        newParams = update(newParams, {
          importedLeId: {
            $set: params.leId,
          },
          leId: {
            $set: "",
          },
        });
        return _.omit(newParams, [
          "creationMode",
          "creationSource",
          "pageName",
        ]);
      }
      return _.omit(newParams, [
        "importedLeId",
        "creationMode",
        "creationSource",
        "pageName",
      ]);
  }
};

export const getAssignmentStateTabsForStaff = () => {
  if (ACLStore.can("FeatureFlag:AssignmentImprovements")) {
    // New order: ALL_POSTS first, then UNREAD_MESSAGES moved after ARCHIVED
    return {
      ALL_POSTS: BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF.ALL_POSTS,
      PUBLISHED: BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF.PUBLISHED,
      ARCHIVED: BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF.ARCHIVED,
      UNREAD_MESSAGES: BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF.UNREAD_MESSAGES,
      SCHEDULED: BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF.SCHEDULED,
      DRAFT: BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF.DRAFT,
    };
  } else {
    // Old order: PUBLISHED first, UNREAD_MESSAGES before ALL_POSTS
    return {
      PUBLISHED: {
        ...BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF.PUBLISHED,
        filters: {
          isFetchToReviewAssigment: true,
        },
      },

      ARCHIVED: BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF.ARCHIVED,

      SCHEDULED: BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF.SCHEDULED,

      DRAFT: BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF.DRAFT,

      UNREAD_MESSAGES: BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF.UNREAD_MESSAGES,

      ALL_POSTS: {
        ...BASE_ASSIGNMENT_STATE_TABS_FOR_STAFF.ALL_POSTS,
        subTitle: "classRoom:all_posts_tab_subtitle", // Old subtitle
        label: "journal:all_posts", // Old title
      },
    };
  }
};

export const getAssignmentStateTabs = (userType = "staff") => {
  if (isUserTypeStaff({ userType })) {
    return getAssignmentStateTabsForStaff();
  }

  return ASSIGNMENT_STATE_TABS;
};

export const getDynamicAssignmentTypeMapping = ({
  assessmentValue,
  curriculumId,
}) => {
  const data = getAssessmentTemplatesFromCache(curriculumId);

  if (!_.isEmpty(data)) {
    const taskTypes = _.get(data, "taskTypes", []);

    const foundOption = _.find(
      taskTypes,
      option =>
        _.toLower(_.get(option, "id", "")) === _.toLower(assessmentValue)
    );

    if (foundOption) {
      const color = _.get(foundOption, "color");
      const avatarIconColor = !_.isEmpty(color) ? color : "violet";

      return {
        typeLabel: _.get(foundOption, "label", assessmentValue),
        avatarIconColor,
        svg: _.get(foundOption, "icon") || (
          <CreateAssessmentSvg
            fill={colors.white}
            fill1={`var(--${avatarIconColor}-500)`}
          />
        ),
      };
    }
  }

  return {
    typeLabel: assessmentValue,
    avatarIconColor: "violet",
    svg: <CreateAssessmentSvg fill={colors.white} fill1={colors.yellow500} />,
  };
};

const CURRICULUM_TYPE_CLASS_TAG_MAPPING_FOR_CALENDAR = {
  IB_PYP: {
    key: "contentTypes",
    value: { ...PYP_ASSIGNMENT_TYPE_MAPPING_FOR_CALENDAR, VIDEO_MEETING },
  },
  IB_MYP: {
    key: "assessmentTypes",
    value: { ...MYP_ASSIGNMENT_TYPE_MAPPING_FOR_CALENDAR, VIDEO_MEETING },
  },
  UBD: {
    key: "assessmentTypes",
    value: { ...UBD_ASSIGNMENT_TYPE_MAPPING_FOR_CALENDAR, VIDEO_MEETING },
  },
  IB_DP: {
    key: "assessmentTypes",
    value: { ...MYP_ASSIGNMENT_TYPE_MAPPING_FOR_CALENDAR, VIDEO_MEETING },
  },
};

export const getClassTagsForCalendar = ({ curriculumType }) => {
  const classTags = _.get(
    CURRICULUM_TYPE_CLASS_TAG_MAPPING_FOR_CALENDAR,
    curriculumType,
    {}
  );
  const filteredValue = _.pickBy(classTags.value, tag => {
    return _.has(tag, "perm") ? ACLStore.can(tag.perm) : true;
  });

  return { ...classTags, value: filteredValue };
};

export const getArchivedStudentAssignmentStatus = () => {
  return getIsEnabledExcusedStatusInAssignment()
    ? ARCHIVED_STUDENT_ASSIGNMENT_STATUS_V2
    : ARCHIVED_STUDENT_ASSIGNMENT_STATUS;
};

export const sendPdftronCommentAddedNotification = ({ id, comment }) => {
  return async () => {
    await client.mutate({
      mutation: sendPdftronCommentAddedMutation,
      variables: {
        id,
        comment,
      },
    });
  };
};

const getAssignmentBulkActionToastMessage = ({ action }) => {
  switch (action) {
    case "EVALUATE_ALL":
      return "toastMsgs:task_successfully_marked_as_evaluated";
    case "EVALUATE_AND_SHARE":
      return "toastMsgs:task_successfully_marked_as_evaluated_and_shared";
    case "SHARE":
      return "toastMsgs:evaluation_successfully_shared";
  }
};

const getAssignmentResponseWarnings = ({ assignmentsResponse }) => {
  const errorCode =
    _.get(assignmentsResponse, ["0", "warning", "errorCode"]) ||
    _.get(assignmentsResponse, ["warning", "errorCode"]) ||
    _.get(assignmentsResponse, ["0", "warning", "code"]) ||
    _.get(assignmentsResponse, ["warning", "code"]);
  const errorText = ATTACHMENT_WARNINGS[errorCode];
  const locale_params = [];
  if (errorCode === "ATTACHMENT_WITHOUT_PERMISSION") {
    const attachmentList = _.get(
      assignmentsResponse,
      ["0", "warning", "metadata", "attachmentList"],
      []
    );
    const attachmentNamesArray = _.map(attachmentList, attachment => {
      const attachmentName = _.get(attachment, "name", "");
      if (_.get(attachment, "type", "") === "IMAGE") {
        return attachmentName?.concat(" (Image)");
      }
      const mimeType = _.get(attachment, "mime_type", null);
      if (mimeType) {
        const attachmentType = _.upperCase(
          getFileExtensionFromMimeType({
            mimeType,
          })
        );
        return attachmentName?.concat(` (${attachmentType})`);
      }
      return attachmentName;
    });
    const attachmentNames = _.join(attachmentNamesArray, ", ");
    locale_params.push({
      key: "attachments",
      value: attachmentNames,
      isPlainText: true,
    });
  }
  return { errorText, locale_params };
};

const getAssignmentResponseWarningsMemoized = _.memoize(
  params => getAssignmentResponseWarnings(params),
  params => JSON.stringify(params)
);

export const getRefetchQueriesForGradebook = ({ courseId, getState }) => {
  const state = getState();
  const organizationId = _.get(state, "login.userInfo.org_id");
  const academicYearId = getSelectedAcademicYearId(state);
  const curriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id"
  );
  const curriculumType = _.get(state, "platform.currentCurriculumProgram.type");

  const filters = _.get(state, "gradebook.assessmentsFilters", {});
  // id of the academic year of the currently selected term in Gradebook
  const gradebookSelectedTermAcademicYearId = _.get(
    state,
    "gradebook.selectedAcademicYearId"
  );
  const { viewBy } = filters;

  const showSameSubjectGradeTasksInGradebook = ACLStore.can(
    "FeatureFlag:ShowSameSubjectGradeTasksInGradebook"
  );

  const primaryGradeId = getCoursePrimaryGradeIdFromCache({
    courseId,
    gradebookSelectedTermAcademicYearId,
    selectedAcademicYearId: academicYearId,
  });

  const isHideTasksExcludedFromTermTotal = getSettingValue({
    name: "HideTasksExcludedFromTermTotalInGradebook",
    currentCurriculumProgramId: curriculumProgramId,
  });

  const isHideTasksFromOtherClasses = getSettingValue({
    name: "HideTasksFromOtherClassesInGradebook",
    currentCurriculumProgramId: curriculumProgramId,
  });

  const categoryIds = _.get(state, "gradebook.categoryIds", []);
  const isCourseBasedGrading = getIsCourseBasedGradingEnabled();
  const learningCourseId = getSelectedLearningCourseId(state);
  const isUsingNewGradingSetupInCourses = getIsConfigurationEnabledForCPAY({
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
    curriculumProgramId,
    academicYearId,
  });

  const refetchQueries = [
    {
      query: getStudentWiseRatingsQuery,
      variables: getGradebookStudentWiseRatingsVariables({
        courseId,
        filters,
        curriculumProgramId,
        academicYearId,
        selectedAcademicYearId: gradebookSelectedTermAcademicYearId,
        showSameSubjectGradeTasksInGradebook,
        isCourseBasedGrading,
        isUsingNewGradingSetupInCourses,
        learningCourseId,
      }),
    },
  ];

  if (ACLStore.can("FeatureFlag:EnableAssessmentGradebookDataGrid")) {
    const gradebookDisplaySettings = _.get(
      state,
      "gradebook.displaySettings",
      {}
    );

    const isAssessmentGradebookHeaderEnhancements = ACLStore.can(
      "FeatureFlag:AssessmentGradebookHeaderEnhancements"
    );

    const isCategoriesView = isAssessmentGradebookHeaderEnhancements
      ? getIsGroupByCategoryView(gradebookDisplaySettings)
      : viewBy === "CATEGORIES";
    const learningCourseId = getSelectedLearningCourseId(state);

    refetchQueries.push({
      query: getGradebookAssignmentsQuery,
      variables: getGradebookAssignmentsVariables({
        learningCourseId,
        courseId,
        filters,
        organizationId,
        curriculumType,
        primaryGradeId,
        showSameSubjectGradeTasksInGradebook,
        isHideTasksFromOtherClasses,
        isHideTasksExcludedFromTermTotal,
        shouldGroupByCategories: isCategoriesView,
        categoryIds,
        displaySettings: gradebookDisplaySettings,
        isAssessmentGradebookHeaderEnhancements,
        curriculumProgramId,
      }),
    });
  } else if (viewBy === "CATEGORIES") {
    refetchQueries.push({
      query: getCategoryWiseAssessmentsQuery,
      variables: getGradebookCategoryWiseAssessmentsVariables({
        courseId,
        filters,
        organizationId,
        curriculumProgramId,
        curriculumType,
        selectedAcademicYearId: gradebookSelectedTermAcademicYearId,
        primaryGradeId,
        showSameSubjectGradeTasksInGradebook,
        isHideTasksFromOtherClasses,
        isHideTasksExcludedFromTermTotal,
      }),
    });
  } else {
    refetchQueries.push({
      query: getOrganizationAssignmentsGradebookQuery,
      variables: getGradebookAllAssessmentsVariables({
        courseId,
        filters,
        organizationId,
        curriculumType,
        primaryGradeId,
        showSameSubjectGradeTasksInGradebook,
        isHideTasksFromOtherClasses,
        isHideTasksExcludedFromTermTotal,
      }),
    });
  }
  return refetchQueries;
};

export const getRefetchQueryForPracticalActivities = ({
  courseId,
  getState,
  isROEP,
}) => {
  const state = getState();
  const psowSearchText = _.get(
    state,
    "practicalActivities.filters.searchText",
    ""
  );
  const roepSearchText = _.get(
    state,
    "recordOfExperimentalProgrammes.filters.searchText",
    ""
  );
  const filters = {
    states: ["PUBLISHED", "ARCHIVED", "SCHEDULED", "DRAFT"],
    orderBy: "PUBLISHED_AT",
    orderByDirection: "DESC",
    unreadMessageCountFilters: {
      includeItemTypesV2: INCLUDE_ITEM_TYPES_FOR_UNREAD_MESSAGES_COUNT,
    },
    assessmentTypes: isROEP ? "dp_ep" : "dp_pa",
    assignmentStateTab: "PUBLISHED",
    searchText: isROEP ? roepSearchText : psowSearchText,
    status: "ALL",
  };
  return {
    query: getCourseAssignmentFeedQuery,
    variables: {
      courseId,
      ...filters,
      first: 20,
    },
  };
};

export const getAssignmentDeletedStatus = async id => {
  const result = await client.query({
    query: getAssignmentDeletedStatusQuery,
    variables: {
      id,
    },
    fetchPolicy: "network-only",
  });

  return _.get(result, "data.node.isDeleted", false);
};

export const updateTeacherRemarkInCache = ({
  assignmentDetailsVariables,
  message,
  academicYearId,
  creatorType = "STAFF",
  isDeleteAiRatings = false,
}) => {
  const variables = {
    ...assignmentDetailsVariables,
    entityEventsFilter: {
      orderBy: "EVENT_TIME",
      orderByDir: "ASC",
    },
    ...(academicYearId ? { userArchivedFilters: { academicYearId } } : {}),
  };
  const cacheData = getStudentAssignmentDetailsFromCache(variables);
  let elementRemarks = _.get(cacheData, "elementRemarks", []);

  // soft delete AI ratings if isDeleteAiRatings is true
  if (isDeleteAiRatings) {
    elementRemarks = _.map(elementRemarks, elementRemark => {
      if (elementRemark.creatorType === "AI") {
        return {
          ...elementRemark,
          isDeleted: true,
        };
      }

      return elementRemark;
    });
  }

  const [comment, restElementRemarks] = _.partition(elementRemarks, {
    creatorType,
    ibPYPElementType: "STUDENT_ASSIGNMENT",
  });

  const commentFallback = {
    ibPYPElementId: null,
    ibPYPElementType: "STUDENT_ASSIGNMENT",
    id: _.get(comment, [0, "id"], generateRandomId()),
    creatorType,
  };

  // update comment message and keep other remarks as it is
  const data = {
    ...(cacheData || {}),
    elementRemarks: [
      {
        ...(_.first(comment) || commentFallback),
        message,
        isDeleted: false,
        __typename: "IbPypElementRemark",
      },
      ...(restElementRemarks || {}),
    ],
  };

  writeStudentAssignmentDetailsInCache({
    ...variables,
    data,
  });
};

export const updateScoreElementRatingInCache = ({
  assignmentDetailsVariables,
  scoreValue,
  type = "SCORE",
  academicCriteriaSet = null,
  academicCriteriaValue = null,
  ibPYPElementId = null,
  academicYearId,
  setIBPYPElementRatingsLocal,
  isDeleteAiRatings,
  isAIResponse,
}) => {
  const variables = {
    ...assignmentDetailsVariables,
    entityEventsFilter: {
      orderBy: "EVENT_TIME",
      orderByDir: "ASC",
    },
    ...(academicYearId ? { userArchivedFilters: { academicYearId } } : {}),
  };
  const cacheData = getStudentAssignmentDetailsFromCache(variables);

  const ibPYPElementRatings = _.get(cacheData, "ibPYPElementRatings", []);

  let hasStaffScoreField;
  let updatedIbPYPElementRatings = _.map(ibPYPElementRatings, item => {
    const { ibPYPElementType, creatorType, isDeleted } = item;
    if (ibPYPElementType === type) {
      // if ai response
      if (creatorType === "AI") {
        // if AI response value has to be updated, isAIResponse is true
        if (isAIResponse) {
          return {
            ...item,
            scoreValue,
            ibPYPElementId,
            academicCriteriaSet,
            academicCriteriaValue,
            value: scoreValue,
            isDeleted: false,
          };
        }

        const _isDeleteAiRatings =
          creatorType === "AI" && !isDeleted && isDeleteAiRatings;

        return {
          ...item,
          ...(_isDeleteAiRatings ? { isDeleted: true } : {}),
        };
      }

      // if staff response
      hasStaffScoreField = true;

      return {
        ...item,
        ibPYPElementId,
        academicCriteriaSet,
        academicCriteriaValue,
        value: scoreValue,
        isDeleted: false,
      };
    }
    return item;
  });

  // if "SCORE" element doesn't exist, then add it with random id
  if (!hasStaffScoreField) {
    updatedIbPYPElementRatings = [
      ...(updatedIbPYPElementRatings || []),
      {
        academicCriteriaSet,
        academicCriteriaValue,
        ibPYPElementId,
        ibPYPElementType: type,
        id: generateRandomId(),
        value: scoreValue,
        source: null,
        isDeleted: false,
        creatorType: "STAFF",
        evaluationJustification: null,
        __typename: "IBPYPElementRating",
      },
    ];
  }

  const data = {
    ...(cacheData || {}),
    ibPYPElementRatings: updatedIbPYPElementRatings,
  };

  if (
    [SCORE, GRADE_SCALE_TOOL].includes(type) &&
    assignmentDetailsVariables?.id &&
    _.isFunction(setIBPYPElementRatingsLocal)
  ) {
    setIBPYPElementRatingsLocal(prevState => {
      return {
        ...prevState,
        [assignmentDetailsVariables.id]: updatedIbPYPElementRatings,
      };
    });

    return;
  }

  writeStudentAssignmentDetailsInCache({
    ...variables,
    data,
  });
};

export const sendStudentAssignmentSubmissionFeedback = input => {
  return async () => {
    try {
      const response = await client.mutate({
        mutation: sendStudentAssignmentSubmissionFeedbackMutation,
        variables: { input },
      });
      return { ...response, successful: true };
    } catch (e) {
      return {
        successful: false,
        errors: ["Something went wrong"],
      };
    }
  };
};

export const updateUserMessageState = ({ input, conversationId }) => {
  return async () => {
    try {
      const response = await client.mutate({
        mutation: updateUserMessageStateMutation,
        variables: { input },
      });
      client.query({
        query: getConversationsUnreadCountQuery,
        variables: {
          id: conversationId,
        },
        fetchPolicy: "network-only",
      });
      return { ...response, successful: true };
    } catch (e) {
      return {
        successful: false,
        errors: ["Something went wrong"],
      };
    }
  };
};

export const registerOfflineTimeTrackingEvents = ({ events }) => {
  return async () => {
    try {
      let eventsToSend = events;
      if (getIsStudent()) {
        const allowedEventTypes = ["START", "RESUME", "PAUSE", "END"];
        eventsToSend = _.filter(events, ({ eventType }) =>
          _.includes(allowedEventTypes, eventType)
        );
      }

      if (_.isEmpty(eventsToSend)) {
        removeTimeTrackingEventsFromLocalStorage();
        return;
      }

      await client.mutate({
        mutation: createEntityEventTrackingMutation,
        variables: { input: eventsToSend },
      });
      removeTimeTrackingEventsFromLocalStorage();
    } catch (e) {}
  };
};

export const createEntityEventTrackingWebView = ({
  entityId,
  entityType,
  eventTime,
  eventType,
  duration = null,
  reason = null,
  debugMetadata = {},
  queryParams,
  callbackOnError,
  metadata = {},
}) => {
  return async () => {
    const oldStudentAssignmentDetails = getStudentAssignmentFromCacheV2(
      queryParams
    );

    const eventsFromLocalStorage =
      eventType !== "PAUSE"
        ? getTimeTrackingEventsFromLocalStorage(entityId)
        : [];

    let currentEventInput = {
      entityId,
      entityType,
      eventTime,
      eventType,
      debugMetadata,
    };

    if (!_.isEmpty(metadata)) {
      _.set(currentEventInput, "metadata", metadata);
    }

    if (eventType === "RESUME") {
      currentEventInput = { ...currentEventInput, duration, reason };
    }

    let eventsToSend = [currentEventInput];
    let mutationResponse = [
      {
        id: generateRandomId(),
        eventType,
        eventTime,
        duration,
        reason,
        __typename: "EntityEventTrack",
      },
    ];

    if (!_.isEmpty(eventsFromLocalStorage)) {
      const filteredLocalEvents = _.filter(
        eventsFromLocalStorage,
        ({ eventTime: currentEventTime }) =>
          !checkIsDateSame({
            date1: currentEventTime,
            date2: eventTime,
            check: "seconds",
          })
      );

      eventsToSend = [...filteredLocalEvents, ...eventsToSend];
      mutationResponse = [
        ..._.map(
          filteredLocalEvents,
          ({ eventType, eventTime, duration = null, reason = null }) => {
            return {
              id: generateRandomId(),
              eventType,
              eventTime,
              duration,
              reason,
              __typename: "EntityEventTrack",
            };
          }
        ),
        ...mutationResponse,
      ];
    }

    if (getIsStudent()) {
      const allowedEventTypes = ["START", "RESUME", "PAUSE", "END"];
      eventsToSend = _.filter(eventsToSend, ({ eventType }) =>
        _.includes(allowedEventTypes, eventType)
      );
      mutationResponse = _.filter(mutationResponse, ({ eventType }) =>
        _.includes(allowedEventTypes, eventType)
      );
    }

    if (_.isEmpty(eventsToSend)) return;

    const refetchQueries = [];
    try {
      const response = await client.mutate({
        mutation: createEntityEventTrackingMutation,
        variables: { input: eventsToSend },
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            __typename: "PlatformMutations",
            createEntityEventTracking: {
              response: mutationResponse,
              warning: null,
              __typename: "GenericCreateEntityEventTrackingResponse",
            },
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
        update: (
          cache,
          {
            data: {
              platform: {
                createEntityEventTracking: { response },
              },
            },
          }
        ) => {
          if (eventType !== "END") {
            const oldEvents = _.get(oldStudentAssignmentDetails, "events", []);
            const newEvents = [...oldEvents, ...response];

            setTimeout(() => {
              const studentAssignmentDetails = getStudentAssignmentFromCacheV2(
                queryParams
              );
              const newStudentAssignmentDetails = {
                ...studentAssignmentDetails,
                events: newEvents,
              };

              writeStudentAssignmentInCacheV2({
                ...queryParams,
                data: newStudentAssignmentDetails,
              });
            });
          }
        },
      });

      if (eventType === "PAUSE" || !_.isEmpty(eventsFromLocalStorage)) {
        removeTimeTrackingEventsFromLocalStorage(entityId);
      }

      if (eventType === "END") {
        // * for `END` eventType, marking isSubmitted as `True` for syncing UI on submit action
        const updatedStudentAssignmentDetails = getStudentAssignmentFromCacheV2(
          queryParams
        );
        if (_.isObject(updatedStudentAssignmentDetails)) {
          writeStudentAssignmentInCacheV2({
            ...queryParams,
            data: {
              ...updatedStudentAssignmentDetails,
              isSubmitted: true,
            },
          });
        }
      }

      return { ...response, successful: true };
    } catch (e) {
      callbackOnError?.();

      if (eventType !== "END") {
        setTimeout(() => {
          writeStudentAssignmentInCacheV2({
            ...queryParams,
            data: oldStudentAssignmentDetails,
          });
        });
      }
      if (e.networkError) {
        return {
          successful: false,
          errors: ["toastMsgs:no_internet_connection"],
        };
      } else {
        return {
          successful: false,
          errors: ["toastMsgs:something_went_wrong"],
        };
      }
    }
  };
};

export const removeElementRatingFromCache = ({
  assignmentDetailsVariables,
  type = "GRADE_SCALE_TOOL",
  ibPYPElementId = null,
  academicYearId,
  setIBPYPElementRatingsLocal,
}) => {
  const variables = {
    ...assignmentDetailsVariables,
    entityEventsFilter: {
      orderBy: "EVENT_TIME",
      orderByDir: "ASC",
    },
    ...(academicYearId ? { userArchivedFilters: { academicYearId } } : {}),
  };
  const cacheData = getStudentAssignmentDetailsFromCache(variables);

  const ibPYPElementRatings = _.get(cacheData, "ibPYPElementRatings", []);

  const updatedIbPYPElementRatings = _.filter(ibPYPElementRatings, item => {
    const {
      ibPYPElementType,
      creatorType,
      ibPYPElementId: itemElementId,
    } = item;
    return !(
      ibPYPElementType === type &&
      creatorType === "STAFF" &&
      itemElementId === ibPYPElementId
    );
  });

  const data = {
    ...(cacheData || {}),
    ibPYPElementRatings: updatedIbPYPElementRatings,
  };

  if (
    assignmentDetailsVariables?.id &&
    _.isFunction(setIBPYPElementRatingsLocal)
  ) {
    setIBPYPElementRatingsLocal(prevState => {
      const currentRatings = _.get(
        prevState,
        assignmentDetailsVariables.id,
        []
      );
      const filteredRatings = _.filter(currentRatings, rating => {
        return !(
          _.get(rating, "ibPYPElementType") === type &&
          _.get(rating, "creatorType") === "STAFF" &&
          _.get(rating, "ibPYPElementId") === ibPYPElementId
        );
      });

      return {
        ...prevState,
        [assignmentDetailsVariables.id]: filteredRatings,
      };
    });

    return;
  }

  writeStudentAssignmentDetailsInCache({
    ...variables,
    data,
  });
};

export const getQueryFilters = ({ filters, academicYearId }) => {
  const {
    contentTypes,
    searchText,
    createdBy,
    orderBy,
    assignmentStateTab,
    folderTopic,
    assessmentTypes,
    excludedAssessmentTypes,
    first,
    courseIds,
    allCourseIds,
    academicTermIds,
    orderByDirection,
    allTermIds,
  } = filters;
  let sendFilters = {};
  const assignmentStateTabs = getAssignmentStateTabs();
  const isEnabledAssignmentImprovements = getIsEnabledAssignmentImprovements();
  if (searchText != "") {
    sendFilters["searchText"] = searchText;
  }
  if (excludedAssessmentTypes) {
    sendFilters["excludedAssessmentTypes"] = excludedAssessmentTypes;
  }
  if (first) {
    sendFilters["first"] = 25;
  }
  if (contentTypes != "ALL") {
    sendFilters["contentTypes"] = [contentTypes];
  }
  if (assessmentTypes != "ALL") {
    sendFilters["assessmentTypes"] = [assessmentTypes];
  }

  if (!_.isEmpty(academicTermIds)) {
    sendFilters["academicTermIds"] = _.sortBy(academicTermIds);
  } else if (!_.isEmpty(allTermIds)) {
    // case when no academic terms are selected in class stream manager
    // send all academicTermIds of user
    sendFilters["academicTermIds"] = _.sortBy(allTermIds);
  }

  if (assignmentStateTab) {
    if (!_.includes(["ALL_POSTS", "UNREAD_MESSAGES"], assignmentStateTab)) {
      sendFilters["states"] = assignmentStateTab;
    }
    if (assignmentStateTab === "ALL_POSTS" && isEnabledAssignmentImprovements) {
      sendFilters["states"] = ["PUBLISHED", "ARCHIVED"];
    }

    if (!_.isEmpty(orderBy)) {
      sendFilters["orderBy"] = orderBy;
    } else {
      sendFilters["orderBy"] = _.get(
        assignmentStateTabs[assignmentStateTab],
        "orderBy",
        ""
      );
    }

    if (!_.isEmpty(orderByDirection)) {
      sendFilters["orderByDirection"] = orderByDirection;
    } else {
      sendFilters["orderByDirection"] = _.get(
        assignmentStateTabs[assignmentStateTab],
        "orderByDirection",
        ""
      );
    }

    sendFilters["unreadMessageCountFilters"] = {
      includeItemTypesV2: INCLUDE_ITEM_TYPES_FOR_UNREAD_MESSAGES_COUNT,
    };

    sendFilters = {
      ...sendFilters,
      ..._.get(assignmentStateTabs, `${assignmentStateTab}.filters`, {}),
    };
  }
  if (folderTopic !== "ALL") {
    sendFilters["folderIds"] = folderTopic;
  }
  if (!_.isEmpty(courseIds)) {
    sendFilters["courseIds"] = _.sortBy(courseIds);
  } else if (!_.isEmpty(allCourseIds)) {
    // case when no courses are selected in class stream manager
    // send all courseIds of user
    sendFilters["courseIds"] = _.sortBy(allCourseIds);
  }

  if (createdBy != "ALL" && !_.isEmpty(createdBy)) {
    if (isEnabledAssignmentImprovements) {
      if (_.isArray(createdBy)) {
        sendFilters["createdByIds"] = createdBy;
      } else {
        sendFilters["createdByIds"] = [createdBy];
      }
    } else {
      sendFilters["createdBy"] = !_.isArray(createdBy) ? createdBy : undefined;
    }
  }

  return {
    ...sendFilters,
    ...(academicYearId ? { userArchivedFilters: { academicYearId } } : {}),
  };
};

export const getQueryFiltersMemoize = _.memoize(
  params => getQueryFilters(params),
  params => JSON.stringify(params)
);

export const getTeacherTasksQueryFilters = ({ filters, userId }) => {
  const {
    contentTypes,
    searchText,
    createdBy,
    orderBy,
    assignmentStateTab,
    folderTopic,
    assessmentTypes,
    excludedAssessmentTypes,
    first,
    courseIds,
    allCourseIds,
    academicTermIds,
    orderByDirection,
  } = filters;
  const assignmentStateTabs = getAssignmentStateTabs();

  const sendFilters = {
    entityTypes: ["ASSIGNMENT"],
  };

  const variables = {
    userId,
    type: "STAFF",
  };

  if (searchText != "") {
    sendFilters["searchText"] = searchText;
  }
  if (excludedAssessmentTypes) {
    variables["excludedAssessmentTypes"] = excludedAssessmentTypes;
  }
  if (first) {
    variables["first"] = 25;
  }
  if (contentTypes != "ALL") {
    sendFilters["contentTypes"] = [contentTypes];
  }
  if (assessmentTypes != "ALL") {
    sendFilters["assessmentTypes"] = [assessmentTypes];
  }
  if (createdBy != "ALL") {
    if (_.isArray(createdBy)) {
      sendFilters["createdByIds"] = createdBy;
    } else {
      sendFilters["createdByIds"] = [createdBy];
    }
  }

  if (!_.isEmpty(academicTermIds)) {
    sendFilters["academicTermIds"] = academicTermIds;
  } else {
    sendFilters["academicTermIds"] = [];
  }

  if (assignmentStateTab) {
    sendFilters["status"] = ASSIGNMENT_TASK_STATUS_MAPPING[assignmentStateTab];

    if (!_.isEmpty(orderBy)) {
      variables["orderBy"] = orderBy;
    } else {
      variables["orderBy"] = _.get(
        assignmentStateTabs[assignmentStateTab],
        "orderBy",
        ""
      );
    }

    if (!_.isEmpty(orderByDirection)) {
      variables["orderByDirection"] = orderByDirection;
    } else {
      variables["orderByDirection"] = _.get(
        assignmentStateTabs[assignmentStateTab],
        "orderByDirection",
        ""
      );
    }
  }
  if (folderTopic !== "ALL") {
    sendFilters["folderIds"] = folderTopic;
  }
  if (!_.isEmpty(courseIds)) {
    sendFilters["courseIds"] = _.sortBy(courseIds);
  } else if (!_.isEmpty(allCourseIds)) {
    // case when no courses are selected in class stream manager
    // send all courseIds of user
    sendFilters["courseIds"] = _.sortBy(allCourseIds);
  }

  variables["filters"] = sendFilters;

  variables["includeClassDiscussion"] = false;
  variables["includeAssignedStudents"] = false;

  return variables;
};

export const getTeacherTasksQueryFiltersMemoize = _.memoize(
  params => getTeacherTasksQueryFilters(params),
  params => JSON.stringify(params)
);

// parent and student filters are managed with this function
export const getFamilyStudentFeedQueryFilters = ({
  filters = {},
  userId,
  userType = "",
  courseId,
  childID,
}) => {
  const {
    assignmentStateTab,
    folderTopic,
    status,
    searchText,
    contentTypes,
    assessmentTypes,
    excludedAssessmentTypes,
  } = filters;
  const sendFilters = {};
  const assignmentStateTabs = getAssignmentStateTabs(userType);

  if (courseId) {
    sendFilters["courseIds"] = courseId;
  }
  if (excludedAssessmentTypes) {
    sendFilters["excludedAssessmentTypes"] = excludedAssessmentTypes;
  }
  if (userType === "parent") {
    sendFilters["studentIds"] = childID;
    sendFilters["userId"] = userId;
    sendFilters["first"] = 10;
  } else if (userType === "student") {
    sendFilters["studentId"] = userId;
  }

  if (searchText != "") {
    sendFilters["searchText"] = searchText;
  }

  if (contentTypes != "ALL") {
    sendFilters["contentTypes"] = [contentTypes];
  }
  if (assessmentTypes != "ALL") {
    sendFilters["assessmentTypes"] = [assessmentTypes];
  }

  if (assignmentStateTab) {
    switch (assignmentStateTab) {
      case "NOT_SUBMITTED":
        sendFilters["states"] = ["PUBLISHED"];
        sendFilters["submissionStatus"] = [
          "NOT_SUBMITTED",
          "RESUBMISSION_NOT_SUBMITTED",
        ];
        break;
      case "ALL_POSTS":
        sendFilters["states"] = ["PUBLISHED", "SCHEDULED", "ARCHIVED"];
        break;
      case "UNREAD_MESSAGES":
        sendFilters["states"] = ["PUBLISHED", "SCHEDULED", "ARCHIVED"];
        sendFilters["isFetchUnreadConverstionAssignment"] = true;
        break;
      case "PUBLISHED":
        sendFilters["states"] = ["PUBLISHED"];
        break;
      default:
        sendFilters["states"] = assignmentStateTab;
    }
    sendFilters["orderBy"] = _.get(
      assignmentStateTabs[assignmentStateTab],
      "orderBy",
      ""
    );
    sendFilters["orderByDirection"] = _.get(
      assignmentStateTabs[assignmentStateTab],
      "orderByDirection",
      ""
    );
    sendFilters["unreadMessageCountFilters"] = {
      includeItemTypesV2: INCLUDE_ITEM_TYPES_FOR_STUDENT_UNREAD_MESSAGES_COUNT,
    };
  }
  if (folderTopic != "ALL") {
    sendFilters["folderIds"] = folderTopic;
  }
  if (status != "ALL") {
    if (_.includes(SUBMISSION_OPTIONS_V2, status)) {
      sendFilters["submissionStatus"] = status;
    } else {
      if (status === "EXCUSED") {
        sendFilters["isEvaluationApplicable"] = false;
      } else {
        sendFilters["status"] = status;
      }
    }
  }
  return sendFilters;
};

export const getCourseAssignmentStateWiseCountV2Filters = ({ filters }) => {
  const createdBy = _.get(filters, "createdBy", "ALL");
  const academicTermIds = _.get(filters, "academicTermIds", []);

  return {
    excludedAssessmentTypes: EXCLUDED_ASSESSMENT_TYPES,
    createdBy:
      createdBy !== "ALL" && !_.isArray(createdBy) ? createdBy : undefined,
    createdByIds:
      createdBy !== "ALL" && _.isArray(createdBy) ? createdBy : undefined,
    academicTermIds: !_.isEmpty(academicTermIds) ? academicTermIds : undefined,
  };
};

export const getOrganizationAssignmentStateWiseCountV2Filters = ({
  filters,
}) => {
  const courseIds = _.get(filters, "courseIds", []);
  const allCourseIds = _.get(filters, "allCourseIds", []);

  return {
    courseIds: !_.isEmpty(courseIds)
      ? _.sortBy(courseIds)
      : _.sortBy(allCourseIds),
    ...getCourseAssignmentStateWiseCountV2Filters({ filters }),
  };
};

export const getCourseGenericFoldersAssignmentFilters = ({ filters }) => {
  const createdBy = _.get(filters, "createdBy", "ALL");
  const academicTermIds = _.get(filters, "academicTermIds", []);

  return {
    academicTermIds: _.sortBy(academicTermIds),
    createdBy:
      createdBy !== "ALL" && !_.isArray(createdBy) ? createdBy : undefined,
    createdByIds:
      createdBy !== "ALL" && _.isArray(createdBy) ? createdBy : undefined,
  };
};

export const upcomingFeedFilters = {
  type: ["COURSE", "PRIMARY"],
  calendarIds: [],
  folderIds: [],
  showUpcoming: true,
  contentTypes: ["ASSIGNMENT_MEETING", "RECURRING_MEETING"],
};

const importFromUnitOption = {
  key: "IMPORT_LE",
  label: "classRoom:import_unit",
  icon: <ImportOutlined variant={"subtle"} />,
  hasTermDependency: true,
  noTermsDisabledTooltip:
    "classRoom:import_from_label_no_grading_periods_tooltip",
};

const checkAreAllTasksInSameYear = ({ assignmentsGroups }) => {
  if (!_.isEmpty(assignmentsGroups)) {
    const assignmentPublishedDates = _.keys(assignmentsGroups);

    const assignmentPublishedYears = _.map(
      assignmentPublishedDates,
      publishedDate => moment(publishedDate).year()
    );

    return _.size(_.uniq(_.compact(assignmentPublishedYears))) === 1;
  }
  return false;
};

export const checkAreAllTasksInSameYearMemoized = _.memoize(
  params => checkAreAllTasksInSameYear(params),
  ({ assignmentsGroups }) => {
    return _.mapValues(assignmentsGroups, assignments =>
      _.map(assignments, "id")
    );
  }
);

const handleLocalToolResponseUpdate = ({
  parentId,
  assessmentToolType,
  isDeleteAiRatings,
  assessmentToolId,
  toolResponsesDataLocal,
  setToolResponsesDataLocal,
  isAcceptAllForTool,
  rowId,
  columnId,
  creatorType,
  inputText,
}) => {
  const assessmentToolResponse = [];
  const assessmentToolResponseIds = [];

  let toolResponsesData = _.get(toolResponsesDataLocal, parentId, []);
  const singlePointRubricResponse = _.find(
    toolResponsesData,
    ({ toolId }) => toolId === assessmentToolId
  )?.toolResponses;

  if (isAcceptAllForTool) {
    _.forEach(singlePointRubricResponse, toolResponse => {
      const {
        id,
        creatorType,
        rowId: rowIdLocal,
        columnId: columnIdLocal,
        subjectiveResponse,
        isDeleted,
      } = toolResponse;

      if (creatorType !== "AI" || isDeleted) return;

      assessmentToolResponse.push({
        rowId: rowIdLocal,
        columnId: columnIdLocal,
        subjectiveResponse,
      });

      assessmentToolResponseIds.push(id);

      toolResponsesData = getUpdatedToolResponses({
        type: assessmentToolType,
        rowId: rowIdLocal,
        columnId: columnIdLocal,
        userEntityType: creatorType,
        assessmentToolId,
        subjectiveResponse,
        isToolResponsesV2: true,
        isDeleteAiRatings,
        toolResponsesData,
      });
    });

    setToolResponsesDataLocal({
      ...toolResponsesDataLocal,
      [parentId]: toolResponsesData,
    });
  } else {
    assessmentToolResponse.push({
      rowId,
      columnId,
      subjectiveResponse: inputText,
    });

    const assessmentToolResponseId = _.find(
      singlePointRubricResponse,
      response =>
        response.rowId === rowId &&
        response.columnId === columnId &&
        response.creatorType === "AI"
    )?.id;

    assessmentToolResponseIds.push(assessmentToolResponseId);

    const updatedToolResponses = getUpdatedToolResponses({
      type: assessmentToolType,
      rowId,
      columnId,
      userEntityType: creatorType,
      assessmentToolId,
      subjectiveResponse: inputText,
      isToolResponsesV2: true,
      isDeleteAiRatings,
      toolResponsesData: _.get(toolResponsesDataLocal, parentId, []),
    });

    setToolResponsesDataLocal({
      ...toolResponsesDataLocal,
      [parentId]: updatedToolResponses,
    });
  }

  return {
    assessmentToolResponse,
    assessmentToolResponseIds,
  };
};

const processAIRemarkApproval = ({ elementRemarks }) => {
  const updatedElementRemarksForCache = [];
  const elementRemarkIdsForMutation = [];
  const elementRemarksForMutation = {};

  // find remarks by creatorType
  const aiElementRemark = _.find(elementRemarks, {
    creatorType: "AI",
    ibPYPElementType: "STUDENT_ASSIGNMENT",
  });

  const staffElementRemark = _.find(elementRemarks, {
    creatorType: "STAFF",
    ibPYPElementType: "STUDENT_ASSIGNMENT",
  });

  // if ai comment present, make it staff comment
  if (aiElementRemark && !aiElementRemark.isDeleted) {
    const { id, message } = aiElementRemark;
    // mark AI response as deleted
    updatedElementRemarksForCache.push({
      ...aiElementRemark,
      isDeleted: true,
    });

    // if staff comment already exists, update the message
    // else create a new staff comment
    if (staffElementRemark) {
      updatedElementRemarksForCache.push({
        ...staffElementRemark,
        message,
      });
    } else {
      updatedElementRemarksForCache.push({
        ...aiElementRemark,
        id: generateRandomId(),
        creatorType: "STAFF",
        isDeleted: false,
      });
    }

    elementRemarkIdsForMutation.push(id);
    elementRemarksForMutation.message = message;
  } else {
    updatedElementRemarksForCache.push(aiElementRemark, staffElementRemark);
  }

  return {
    updatedElementRemarksForCache: _.compact(updatedElementRemarksForCache),
    elementRemarkIdsForMutation,
    elementRemarksForMutation,
  };
};

export const deleteAIEvaluationJob = ({ jobId, studentAssignmentId }) => {
  return async () => {
    const res = await client.mutate({
      mutation: deleteJobMutation,
      variables: {
        input: {
          jobId,
        },
      },
      refetchQueries: mutationResult => {
        const isSuccess = _.get(
          mutationResult,
          "data.integration.integrationDeleteJob",
          false
        );
        return isSuccess
          ? [
              {
                query: getStudentAiEvaluationJobStatusQuery,
                variables: { id: studentAssignmentId },
              },
            ]
          : [];
      },
      awaitRefetchQueries: true,
    });

    return res?.data;
  };
};

export const keepStudentAssignmentAIEvaluation = ({
  jobId,
  studentAssignmentId,
  isKeepAiEvaluation,
}) => {
  return async () => {
    const res = await client.mutate({
      mutation: keepStudentAssignmentAIEvaluationMutation,
      variables: {
        input: {
          jobId,
          isKeepAiEvaluation,
        },
      },
      refetchQueries: mutationResult => {
        const isSuccess = _.get(
          mutationResult,
          "data.platform.keepStudentAssignmentAIevaluation.isSuccess",
          false
        );
        return isSuccess
          ? [
              {
                query: getStudentAiEvaluationJobStatusQuery,
                variables: { id: studentAssignmentId },
              },
            ]
          : [];
      },
      awaitRefetchQueries: true,
    });

    return res?.data;
  };
};

export const validateAssessmentLink = ({ assessmentId }) => {
  return async () => {
    const response = await client.mutate({
      mutation: validateAssessmentLinkMutation,
      variables: {
        input: {
          assessmentId,
        },
      },
    });

    return _.get(
      response,
      "data.planner.validateAssessmentLink.allLinksValid",
      false
    );
  };
};

const removeAssignmentFromCache = ({
  readCacheQueryFn,
  queryVariables,
  pathString,
  assignmentId,
  writeCacheQueryFn,
  courseId,
  userId,
}) => {
  const cacheData = readCacheQueryFn(queryVariables);
  if (_.isEmpty(cacheData)) return {};

  const edgesPath = `${pathString}.edges`;
  const idPath = pathString === "tasks" ? "item.id" : "node.id";
  const edges = _.get(cacheData, edgesPath, []);
  const removedIndex = _.findIndex(
    edges,
    edge => _.get(edge, idPath) === assignmentId
  );
  if (removedIndex === -1) return {};

  const removedAssignment = edges[removedIndex];
  // Store adjacent assignment IDs for proper placement during undo
  const prevAssignmentId =
    removedIndex > 0 ? _.get(edges[removedIndex - 1], idPath) : null;
  const nextAssignmentId =
    removedIndex < edges.length - 1
      ? _.get(edges[removedIndex + 1], idPath)
      : null;
  const updatedAssignmentEdges = _.filter(
    edges,
    (_, idx) => idx !== removedIndex
  );

  const updatedCacheData = {
    node: {
      __typename: pathString === "tasks" ? "Staff" : "Course",
      id: pathString === "tasks" ? userId : courseId,
      ...cacheData,
      [pathString]: {
        ..._.get(cacheData, pathString, {}),
        edges: updatedAssignmentEdges,
        totalCount: _.get(cacheData, `${pathString}.totalCount`, 0) - 1,
      },
    },
  };

  writeCacheQueryFn({
    ...(pathString === "tasks" ? { userId } : { courseId }),
    variables: queryVariables,
    data: updatedCacheData,
  });

  return {
    removedAssignment,
    removedIndex,
    prevAssignmentId,
    nextAssignmentId,
  };
};

const addAssignmentToCache = ({
  readCacheQueryFn,
  queryVariables,
  pathString,
  assignment,
  index,
  writeCacheQueryFn,
  courseId,
  userId,
  prevAssignmentId,
  nextAssignmentId,
}) => {
  const cacheData = readCacheQueryFn(queryVariables);
  if (_.isEmpty(cacheData)) return;

  const edgesPath = `${pathString}.edges`;
  const edges = _.get(cacheData, edgesPath, []);
  const idPath = pathString === "tasks" ? "item.id" : "node.id";

  let insertIndex = 0;

  if (prevAssignmentId || nextAssignmentId) {
    // Try to find the correct position based on adjacent assignments
    if (prevAssignmentId) {
      const prevIndex = _.findIndex(
        edges,
        edge => _.get(edge, idPath) === prevAssignmentId
      );
      if (prevIndex !== -1) {
        insertIndex = prevIndex + 1;
      }
    } else if (nextAssignmentId) {
      const nextIndex = _.findIndex(
        edges,
        edge => _.get(edge, idPath) === nextAssignmentId
      );
      if (nextIndex !== -1) {
        insertIndex = nextIndex;
      }
    }
  } else {
    // Fallback to original index if no adjacent assignments found
    insertIndex = Math.min(index, edges.length);
  }

  const updatedAssignmentEdges = [...edges];
  updatedAssignmentEdges.splice(insertIndex, 0, assignment);

  const updatedCacheData = {
    node: {
      __typename: pathString === "tasks" ? "Staff" : "Course",
      id: pathString === "tasks" ? userId : courseId,
      ...cacheData,
      [pathString]: {
        ..._.get(cacheData, pathString, {}),
        edges: updatedAssignmentEdges,
        totalCount: _.get(cacheData, `${pathString}.totalCount`, 0) + 1,
      },
    },
  };

  writeCacheQueryFn({
    ...(pathString === "tasks" ? { userId } : { courseId }),
    variables: queryVariables,
    data: updatedCacheData,
  });
};

// Auto-generated exports
export {
  getMutationParams,
  CURRICULUM_TYPE_CLASS_TAG_MAPPING_FOR_CALENDAR,
  getAssignmentBulkActionToastMessage,
  getAssignmentResponseWarnings,
  getAssignmentResponseWarningsMemoized,
  importFromUnitOption,
  checkAreAllTasksInSameYear,
  handleLocalToolResponseUpdate,
  processAIRemarkApproval,
  removeAssignmentFromCache,
  addAssignmentToCache,
};

export const getTimeTrackingEventsFromLocalStorage = (entityId = null) => {
  let localEvents = getDecryptedJsonItem("STUDENT_ASSIGNMENT_EVENTS");
  if (_.isEmpty(localEvents)) {
    return [];
  }

  localEvents = JSON.parse(localEvents);
  if (_.isEmpty(entityId)) {
    return localEvents;
  }

  return _.filter(
    localEvents,
    ({ entityId: eventEntityId }) => eventEntityId == entityId
  );
};

export const removeTimeTrackingEventsFromLocalStorage = (entityId = null) => {
  if (_.isEmpty(entityId)) {
    clearLocalStorageForKey("STUDENT_ASSIGNMENT_EVENTS");
    return;
  }

  const eventsFromLocalStorage = getTimeTrackingEventsFromLocalStorage();

  const updatedEvents = _.filter(
    eventsFromLocalStorage,
    ({ entityId: eventEntityId }) => eventEntityId != entityId
  );

  if (_.isEmpty(updatedEvents)) {
    clearLocalStorageForKey("STUDENT_ASSIGNMENT_EVENTS");
    return;
  }

  setEncryptedJsonItem({
    key: "STUDENT_ASSIGNMENT_EVENTS",
    value: JSON.stringify(updatedEvents),
  });
};
